package fr.frux.feature.treeadd.wizard

import fr.frux.core.common.logging.Logger
import fr.frux.core.common.result.runCatchingCancellable
import fr.frux.core.maps.Location
import fr.frux.core.media.PickedImage
import fr.frux.core.media.PreparedPhoto
import fr.frux.feature.treeadd.FIRST_CONTRIBUTION_CHECK_TIMEOUT_MS
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.photos.PhotoUpload
import fr.frux.shared.domain.photos.PhotoUploadRequest
import fr.frux.shared.domain.photos.PhotoUploadState
import fr.frux.shared.domain.photos.TreePhotoUploads
import fr.frux.shared.domain.repository.TreeRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Deferred
import kotlinx.coroutines.Job
import kotlinx.coroutines.async
import kotlinx.coroutines.channels.SendChannel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.getAndUpdate
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.transformWhile
import kotlinx.coroutines.launch
import kotlinx.coroutines.withTimeoutOrNull

private const val TAG = "WizardSubmitter"

/**
 * Soumission de l'assistant, extraite de [TreeWizardViewModel] (detekt `TooManyFunctions`) : un
 * seul `POST /trees` (espèce, certitude, position, note, état facultatif), puis l'enfilement des
 * photos dans [photoUploads], dont la progression est relayée par `WizardSubmission.Uploading`.
 *
 * [idempotencyKey] est constante : un réessai après échec rejoue la même création. Le passage à
 * `Creating` est atomique, donc deux appuis rapprochés ne produisent qu'un envoi.
 */
internal class WizardSubmitter(
    private val treeRepository: TreeRepository,
    private val photoUploads: TreePhotoUploads,
    private val scope: CoroutineScope,
    private val state: MutableStateFlow<TreeWizardState>,
    private val events: SendChannel<TreeWizardEvent>,
    private val idempotencyKey: String,
    private val preparePhoto: suspend (PickedImage) -> PreparedPhoto,
    private val isFirstContribution: suspend () -> Boolean,
) {
    private var watcher: Job? = null

    // Relecture « première contribution », lancée dès la création réussie et bornée à 3 s.
    private var firstContribution: Deferred<Boolean>? = null

    // Une fois la sortie réclamée, plus rien ne doit émettre un second événement de sortie.
    private var closing = false

    /**
     * Sans effet pendant `Creating`. Pendant `Uploading`, le bouton sert à quitter sans attendre
     * ([leaveNow]). Depuis `Idle`, lance la création.
     */
    fun submit() {
        val before =
            state.getAndUpdate {
                if (it.submission == WizardSubmission.Idle) {
                    it.copy(submission = WizardSubmission.Creating, submitFailed = false)
                } else {
                    it
                }
            }
        when (before.submission) {
            WizardSubmission.Idle -> scope.launch { create(before) }
            is WizardSubmission.Uploading -> leaveNow()
            WizardSubmission.Creating -> Unit
        }
    }

    /** Quitte pendant `Uploading` : l'arbre existe déjà, la file continue sans l'assistant. */
    fun leaveNow() {
        val current = state.value.submission as? WizardSubmission.Uploading
        val first = firstContribution
        if (current != null && first != null && claimExit()) {
            watcher?.cancel()
            scope.launch { events.trySend(TreeWizardEvent.Created(current.tree, false, first.await())) }
        }
    }

    private suspend fun create(snapshot: TreeWizardState) {
        val species = snapshot.species.selected
        val position = snapshot.position
        if (species == null || position == null) {
            Logger.e(TAG, "Soumission sans espèce ou sans position, retour à l'étape Détails")
            state.getAndUpdate { it.copy(submission = WizardSubmission.Idle) }
            return
        }
        send(snapshot, species, position)
            .onSuccess { tree -> afterCreation(tree, snapshot.details.photos) }
            .onFailure { error ->
                Logger.e(TAG, "Échec de la création d'arbre", error)
                state.getAndUpdate { it.copy(submission = WizardSubmission.Idle, submitFailed = true) }
            }
    }

    private suspend fun send(
        snapshot: TreeWizardState,
        species: Species,
        position: Location,
    ): Result<Tree> =
        treeRepository.createTree(
            speciesId = species.id,
            latitude = position.latitude,
            longitude = position.longitude,
            status = snapshot.details.status,
            certainty = snapshot.species.certainty,
            description =
                snapshot.details.note
                    .trim()
                    .ifEmpty { null },
            idempotencyKey = idempotencyKey,
        )

    private suspend fun afterCreation(
        tree: Tree,
        photos: List<WizardPhoto>,
    ) {
        val first = scope.async { checkFirstContribution() }
        firstContribution = first
        if (photos.isEmpty()) {
            if (claimExit()) events.trySend(TreeWizardEvent.Created(tree, false, first.await()))
            return
        }
        val requests =
            photos.map { photo ->
                val prepared = photo.prepared ?: prepareOrOriginal(photo.image)
                PhotoUploadRequest(photo.id, prepared.bytes, prepared.contentType)
            }
        photoUploads.enqueue(tree.id, requests)
        val ids = requests.map { it.id }
        // Uploading dès l'enfilement : à partir de là, quitter est sans perte.
        state.getAndUpdate {
            it.copy(submission = WizardSubmission.Uploading(tree, emptyList()))
        }
        watcher = scope.launch { watch(tree, first, ids) }
    }

    private suspend fun prepareOrOriginal(image: PickedImage): PreparedPhoto =
        runCatchingCancellable { preparePhoto(image) }.getOrElse { error ->
            // Sans version préparée, la photo part telle quelle plutôt que de bloquer l'ajout.
            Logger.e(TAG, "Préparation de la photo impossible à l'envoi", error)
            PreparedPhoto(image.bytes)
        }

    private suspend fun checkFirstContribution(): Boolean =
        runCatchingCancellable {
            withTimeoutOrNull(FIRST_CONTRIBUTION_CHECK_TIMEOUT_MS) { isFirstContribution() }
        }.getOrNull() ?: false

    private suspend fun watch(
        tree: Tree,
        first: Deferred<Boolean>,
        ids: List<String>,
    ) {
        var last: List<PhotoUpload> = emptyList()
        photoUploads.uploads
            .map { byTree -> byTree[tree.id].orEmpty().filter { it.id in ids } }
            // Une liste incomplète juste après l'enfilement n'est pas « terminée ».
            .transformWhile { list ->
                emit(list)
                !(list.size == ids.size && list.all { it.isDone() })
            }.collect { list ->
                last = list
                state.getAndUpdate {
                    if (closing) it else it.copy(submission = WizardSubmission.Uploading(tree, list))
                }
            }
        if (claimExit()) {
            val failed = last.any { it.state == PhotoUploadState.Failed }
            events.trySend(TreeWizardEvent.Created(tree, failed, first.await()))
        }
    }

    private fun PhotoUpload.isDone(): Boolean = state == PhotoUploadState.Sent || state == PhotoUploadState.Failed

    private fun claimExit(): Boolean {
        if (closing) return false
        closing = true
        return true
    }
}
